import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithErrorHandling } from "../baseQuery";

export const clientsApi = createApi({
  reducerPath: "clientsApi",
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ["Clients"],
  refetchOnFocus: true,
  refetchOnReconnect: true,

  endpoints: (builder) => ({
    // Get All Clients
    getAllClients: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        sortBy = "display_order",
        order = "asc",
      } = {}) => ({
        url: "/clients",
        params: { page, limit, search, sortby: sortBy, order },
      }),

      /* Normalize API response */
      transformResponse: (response) => ({
        data: response?.data ?? [],
        total: response?.total ?? 0,
        page: response?.page ?? 1,
        limit: response?.limit ?? 10,
        totalPages: response?.totalPages ?? 1,
      }),

      /* Correct tag strategy */
      providesTags: (result) =>
        result?.data?.length
          ? [
              ...result.data.map(({ id }) => ({
                type: "Clients",
                id: Number(id),
              })),
              { type: "Clients", id: "LIST" },
            ]
          : [{ type: "Clients", id: "LIST" }],

      keepUnusedDataFor: 60,
      refetchOnMountOrArgChange: true,
    }),

    // Get Client By ID
    getClientById: builder.query({
      query: (id) => `/clients/${id}`,
      transformResponse: (response) =>
        response?.data ? response.data : response,
      providesTags: (result, error, id) => [{ type: "Clients", id }],
    }),

    // Create Client
    createClient: builder.mutation({
      query: (data) => ({
        url: "/clients",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Clients", id: "LIST" }],
    }),

    // Update Client
    updateClient: builder.mutation({
      query: ({ id, data }) => ({
        url: `/clients/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Clients", id },
        { type: "Clients", id: "LIST" },
      ],
    }),

    // Delete Client
    deleteClient: builder.mutation({
      query: (id) => ({
        url: `/clients/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Clients", id: "LIST" }],
    }),

    /* Toggle Client Status */
    toggleClientStatus: builder.mutation({
      query: (id) => ({
        url: `/clients/${id}/toggle`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Clients", id },
        { type: "Clients", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetAllClientsQuery,
  useGetClientByIdQuery,
  useCreateClientMutation,
  useUpdateClientMutation,
  useDeleteClientMutation,
  useToggleClientStatusMutation,
} = clientsApi;
